/**
 * Stable, dependency-free hashing & id helpers.
 *
 * `hashCombination` produces a deterministic id from a set of parameter
 * assignments so that identical combinations collapse to the same record
 * regardless of which user or session created them.
 */

import type { ParameterAssignment } from "@/types";

/** FNV-1a 32-bit hash, rendered as base36. Fast and collision-resistant enough
 * for a per-workbook combination keyspace. */
export function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts to stay in integer range.
    hash = Math.imul(hash, 0x01000193);
  }
  // >>> 0 coerces to unsigned 32-bit.
  return (hash >>> 0).toString(36);
}

/**
 * Deterministic combination id. Assignments are sorted by parameterId so order
 * of entry does not affect identity, then joined with their canonical values.
 */
export function hashCombination(assignments: ParameterAssignment[]): string {
  const canonical = [...assignments]
    .sort((a, b) => a.parameterId.localeCompare(b.parameterId))
    .map((a) => `${a.parameterId}=${stringifyValue(a.value)}`)
    .join("&");
  return `cmb_${fnv1a(canonical)}`;
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return " ";
  return String(value);
}

/** RFC4122-ish unique id. Uses crypto.randomUUID when available. */
export function uid(prefix = "id"): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return `${prefix}_${g.crypto.randomUUID()}`;
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Pipe-joined human signature, e.g. "Texas | 2025 | Medicare". */
export function buildSignature(assignments: ParameterAssignment[]): string {
  return assignments.map((a) => a.displayValue).join(" | ");
}
